import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import ContactFormModal from '../components/ContactFormModal';
import IconButton from '../components/IconButton';

const ContactRow = ({ contact, onEdit, onDelete }) => (
  <tr>
    <td>{contact.name}</td>
    <td>{contact.title || '-'}</td>
    <td>{contact.email || '-'}</td>
    <td>{contact.phone || '-'}</td>
    <td>{contact.is_primary ? 'Yes' : 'No'}</td>
    <td>
      <IconButton title="Edit contact" onClick={() => onEdit(contact)}>
        <svg viewBox="0 0 24 24" fill="none"><path d="M3 21v-3.6l11-11L20.6 9 9.6 20H3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </IconButton>
      <IconButton title="Delete contact" onClick={() => onDelete(contact.id)}>
        <svg viewBox="0 0 24 24" fill="none"><path d="M3 6h18" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </IconButton>
    </td>
  </tr>
);

const ClientContacts = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // modal/form state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  const openAddModal = () => { setEditingContact(null); setShowFormModal(true); };
  const openEditModal = (c) => { setEditingContact(c); setShowFormModal(true); };
  const closeFormModal = () => { setEditingContact(null); setShowFormModal(false); };



  const fetchClient = async () => {
    try {
      const res = await api.get(`/clients/${id}`);
      setClient(res.data.data);
      setError(null);
      return true;
    } catch (err) {
      setClient(null);
      setError(err.response?.data?.error || 'Client not found');
      return false;
    }
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/clients/${id}/contacts`);
      setContacts(res.data.data || []);
      setError(null);
    } catch (err) {
      setContacts([]);
      setError(err.response?.data?.error || 'Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const ok = await fetchClient();
      if (ok) await fetchContacts();
    })();
    // eslint-disable-next-line
  }, [id]);

  const handleAddClick = () => { openAddModal(); };

  const handleEdit = (c) => { openEditModal(c); };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await api.delete(`/clients/${id}/contacts/${contactId}`);
      await fetchContacts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete contact');
    }
  };

  const onFormSaved = async () => {
    await fetchContacts();
  };

  if (error && !client) {
    return (
      <div className="clients-container">
        <div className="table-header">
          <div>
            <h2>Contacts</h2>
            <div style={{ marginTop: 6 }}>
              <button className="back-btn" onClick={() => navigate('/clients')}>Back to Clients</button>
            </div>
          </div>
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="clients-container">
      <div className="table-header">
        <div>
          <h2>Contacts{client ? ` — ${client.name}` : ''}</h2>
          <div style={{ marginTop: 6 }}>
            <button className="back-btn" onClick={() => navigate('/clients')}>Back to Clients</button>
          </div>
        </div>
        <div>
          <button className="add-account-btn" onClick={handleAddClick}>Add Contact</button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div style={{ display: 'flex', gap: 20 }}>
        <div style={{ flex: 1 }} className="table-responsive">
          {loading ? (
            <div>Loading...</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Title</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Is_Primary</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 ? (
                  <tr><td colSpan={6}>No contacts</td></tr>
                ) : contacts.map(c => (
                  <ContactRow key={c.id} contact={c} onEdit={handleEdit} onDelete={handleDelete} />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Contact Form is shown in modal. */}
        {showFormModal && (
          <ContactFormModal clientId={id} contact={editingContact} onClose={closeFormModal} onSaved={onFormSaved} />
        )}
      </div>

      <style jsx>{`
        .clients-container { padding: 20px; max-width: 1200px; margin: 0 auto; }
        .table-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:20px; }
        .add-account-btn { background-color: #00875a; color: white; padding:8px 12px; border-radius:4px; }
        .back-btn { padding:8px 12px; border-radius:4px; background:#f4f5f7; }
        .table { width:100%; border-collapse: collapse; }
        .table th, .table td { border: 1px solid #e0e0e0; padding: 8px; }
        .form-group { margin-bottom: 10px; }
        .form-group input, .form-group textarea { width:100%; padding:6px 8px; }
        .save-btn, .cancel-btn, .close-btn { padding:8px 12px; }
        .edit-btn { background:#00875a; color:#fff; padding:6px 8px; margin-right:8px; }
        .delete-btn { background:#de350b; color:#fff; padding:6px 8px; }
      `}</style>
    </div>
  );
};

export default ClientContacts;
